import { db, SyncQueueItem } from '@/shared/lib/db'
import { hydrateUserData } from '@/shared/lib/dbSync'
import { firestore, storage } from '@/shared/lib/firebase'
import { deleteDoc, doc, setDoc } from 'firebase/firestore'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'

export class SyncService {
    private isSyncing = false
    private userId: string | null = null

    private removeUndefinedDeep<T>(value: T): T {
        if (Array.isArray(value)) {
            return value.map((item) => this.removeUndefinedDeep(item)) as T
        }

        if (value instanceof Date || value === null || typeof value !== 'object') {
            return value
        }

        const cleanedEntries = Object.entries(value as Record<string, unknown>)
            .filter(([, entryValue]) => entryValue !== undefined)
            .map(([key, entryValue]) => [key, this.removeUndefinedDeep(entryValue)])

        return Object.fromEntries(cleanedEntries) as T
    }

    setUserId(uid: string | null) {
        this.userId = uid
    }

    async initializeForUser(uid: string) {
        this.setUserId(uid)

        try {
            await this.processQueue()
            await hydrateUserData(uid)
            await this.processQueue()
        } catch (error) {
            console.error('Failed to initialize sync for user:', error)
        }
    }

    async processQueue() {
        if (this.isSyncing || !this.userId || !navigator.onLine) return
        this.isSyncing = true

        try {
            // Get all pending items and failed items that explicitly need retry
            // For simplicity, we'll fetch both. In a real app, query optimization is needed.
            const pendingItems = await db.sync_queue.where('status').equals('PENDING').toArray()
            const failedItems = await db.sync_queue.where('status').equals('FAILED').toArray()

            const itemsToSync = [...pendingItems, ...failedItems].sort(
                (a, b) => a.created_at.getTime() - b.created_at.getTime()
            )

            for (const item of itemsToSync) {
                if (item.status === 'FAILED' && item.retryCount > 5) {
                    console.log(`Skipping item ${item.id} due to max retries`)
                    continue
                }
                await this.processItem(item)
            }
        } catch (error) {
            console.error('Error processing sync queue:', error)
        } finally {
            this.isSyncing = false
        }
    }

    private async processItem(item: SyncQueueItem & { id: string }) {
        try {
            await this.syncToFirestore(item)

            // On success, delete from queue
            await db.sync_queue.delete(item.id)
            console.log(`Synced item ${item.id} (${item.operation} ${item.entityTable})`)
        } catch (error) {
            console.error(`Failed to sync item ${item.id}:`, error)
            await db.sync_queue.update(item.id, {
                status: 'FAILED',
                retryCount: (item.retryCount || 0) + 1,
                error: (error as any).message,
                updated_at: new Date(),
            })
        }
    }

    private async syncToFirestore(item: SyncQueueItem) {
        if (!this.userId) throw new Error('User ID not set for SyncService')

        const payload = item.payload
        if (!payload && item.operation !== 'DELETE') {
            throw new Error('Payload missing for non-DELETE operation')
        }

        let path = ''
        const userId = this.userId

        switch (item.entityTable) {
            case 'project':
                path = `users/${userId}/projects/${item.entityId}`
                break
            case 'storyboards': { // Ideally, SyncQueueItem should store 'parentId' or similar metadata. // For now, assuming payload exists or we can derive it. // If DELETE, we might not have payload... which is a problem for path generation if generic. // We need projectId from payload or somewhere. // Storyboards are subcollections of projects: users/{uid}/projects/{pid}/storyboards/{sid}
                const projectId = payload?.projectId
                if (!projectId) {
                    // Fallback or Error?
                    // If DELETE, we can't find path without projectId.
                    throw new Error('Cannot sync storyboard without projectId')
                }
                path = `users/${userId}/projects/${projectId}/storyboards/${item.entityId}`
                
                // Handle manual image uploads for frames
                if (item.operation !== 'DELETE' && payload.frames) {
                    await this.handleStoryboardImageUploads(item.entityId, payload, userId)
                }
                break
            }
            case 'shotlists': {
                // users/{userId}/projects/{projectId}/shotlists/{shotListId}
                const slProjectId = payload?.projectId
                if (!slProjectId) throw new Error('Cannot sync shotlist without projectId')
                path = `users/${userId}/projects/${slProjectId}/shotlists/${item.entityId}`
                break
            }
            case 'story': // Legacy: handle old items queued with singular name
            case 'stories': {
                const stProjectId2 = payload?.projectId
                if (!stProjectId2) throw new Error('Cannot sync story without projectId')
                path = `users/${userId}/projects/${stProjectId2}/stories/${item.entityId}`
                break
            }
            case 'screenplays': {
                const spProjectId = payload?.projectId
                if (!spProjectId) throw new Error('Cannot sync screenplay without projectId')
                path = `users/${userId}/projects/${spProjectId}/screenplays/${item.entityId}`
                break
            }
            case 'characters': {
                const charProjectId = payload?.projectId
                if (!charProjectId) throw new Error('Cannot sync character without projectId')
                path = `users/${userId}/projects/${charProjectId}/characters/${item.entityId}`

                // Handle manual image uploads if any
                if (item.operation !== 'DELETE' && payload.variants) {
                    await this.handleCharacterImageUploads(item.entityId, payload, userId)
                }
                break
            }
            default:
                console.warn(`Unknown entity table: ${item.entityTable}`)
                return
        }

        if (!path) {
            throw new Error(`Invalid path generated for ${item.entityTable}`)
        }

        const docRef = doc(firestore, path)

        if (item.operation === 'DELETE') {
            await deleteDoc(docRef)
        } else {
            const sanitizedPayload = this.removeUndefinedDeep(payload)
            // MERGE true to avoid overwriting unrelated fields if any
            await setDoc(docRef, sanitizedPayload, { merge: true })
        }
    }

    private async handleCharacterImageUploads(characterId: string, payload: any, userId: string) {
        let hasChanges = false

        for (const variant of payload.variants || []) {
            // If user_image is a File or Blob, upload it
            if (variant.user_image instanceof File || variant.user_image instanceof Blob) {
                const fileExtension = (variant.user_image as File).name?.split('.').pop() || 'jpg'
                const storagePath = `character-image-uploads/${characterId}-${variant.id}.${fileExtension}`
                const storageRef = ref(storage, storagePath)

                console.log(`Uploading manual image for variant ${variant.id}...`)
                await uploadBytes(storageRef, variant.user_image)
                const downloadUrl = await getDownloadURL(storageRef)

                // Update payload with the permanent URL
                variant.user_image = downloadUrl
                hasChanges = true
            }
        }

        if (hasChanges) {
            // Update local DB since we now have the permanent URL
            await db.characters.update(characterId, { variants: payload.variants })
        }
    }

    private async handleStoryboardImageUploads(storyboardId: string, payload: any, userId: string) {
        let hasChanges = false

        for (const frame of payload.frames || []) {
            if (frame.imageUrl instanceof File || frame.imageUrl instanceof Blob) {
                const fileExtension = (frame.imageUrl as File).name?.split('.').pop() || 'jpg'
                const storagePath = `storyboard-image-uploads/${payload.projectId}-${payload.sceneId}-${frame.id}.${fileExtension}`
                const storageRef = ref(storage, storagePath)

                console.log(`Uploading manual image for storyboard frame ${frame.id}...`)
                await uploadBytes(storageRef, frame.imageUrl)
                const downloadUrl = await getDownloadURL(storageRef)

                frame.imageUrl = downloadUrl
                hasChanges = true
            }
        }

        if (hasChanges) {
            await db.storyboards.update(storyboardId, { frames: payload.frames })
        }
    }
}

export const syncService = new SyncService()
